type ScreenSizeName = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type ExtendedScreenSizeName =
  | 'xs'
  | 'sm'
  | 'base'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | '8xl'
  | '9xl'

const screenSizeNames: ScreenSizeName[] = ['xs', 'sm', 'md', 'lg', 'xl']

export const PIXELS_PER_REM = 16

export const screens: Record<ScreenSizeName, string> = {
  xs: '480px',
  sm: '768px',
  md: '940px',
  lg: '1200px',
  xl: '1600px',
}

export const fontSizes: Record<ExtendedScreenSizeName, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
  '8xl': '6rem',
  '9xl': '8rem',
}

const range = (start: number, end: number) =>
  Array(end)
    .fill(undefined)
    .map((_x, i) => `${start + i}`)

const generateClasses = (
  property: string,
  values: string[],
  responsive = true,
  negative = false
) => {
  const classes: string[] = []

  for (const value of values) {
    const c = `${property}-${value}`
    classes.push(c)

    if (negative) {
      classes.push(`-${c}`)
    }

    if (responsive) {
      for (const screenSizeName of screenSizeNames) {
        classes.push(`${screenSizeName}:${c}`)

        if (negative) {
          classes.push(`-${screenSizeName}:${c}`)
        }
      }
    }
  }

  return classes
}

export const clampBuilder = (minFontSize: number, maxFontSize: number) => {
  const minWidthPx = Number(screens.xs.replace('px', ''))
  const maxWidthPx = Number(screens.md.replace('px', ''))

  const minWidth = minWidthPx / PIXELS_PER_REM
  const maxWidth = maxWidthPx / PIXELS_PER_REM

  const slope = (maxFontSize - minFontSize) / (maxWidth - minWidth)
  const yAxisIntersection = -minWidth * slope + minFontSize

  return `clamp(${minFontSize}rem, ${yAxisIntersection}rem + ${
    slope * 100
  }vw, ${maxFontSize}rem )`
}

export const safelist = [
  ...generateClasses('text', ['left', 'center', 'right', 'justify']),
  ...generateClasses('animate', ['none', 'spin', 'ping', 'pulse', 'bounce']),
  ...generateClasses('grid-cols', [...range(1, 12), 'none']),
  ...generateClasses('col-span', [...range(1, 12), 'full']),
  ...generateClasses('col-start', [...range(1, 13), 'auto']),
  ...generateClasses(
    'order',
    [...range(0, 37), 'first', 'last', 'none'],
    true,
    true
  ),
  ...generateClasses('self', ['auto', 'start', 'end', 'center', 'stretch']),
  ...generateClasses('justify-self', [
    'auto',
    'start',
    'end',
    'center',
    'stretch',
  ]),
  ...generateClasses('max-w', [
    '0',
    'none',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
    '5xl',
    '6xl',
    '7x',
    'full',
    'min',
    'max',
    'prose',
    'screen-sm',
    'screen-md',
    'screen-lg',
    'screen-xl',
    'screen-2xl',
  ]),
  ...generateClasses('min-h', ['screen', 'xs', 'sm', 'md', 'lg', 'xl']),
  ...generateClasses('pt', [
    '[100%]',
    '[calc((3/4)*100%)]',
    '[calc((7/5)*100%)]',
    '[calc((6/4)*100%)]',
    '[calc((9/16)*100%)]',
    '[calc((7/16)*100%)]',
  ]),
]

export const colors = {
  transparent: 'transparent',
  current: 'currentColor',
  dark: '#000000',
  white: '#FFFFFF',
  gray: {
    lighter: '#969696',
    DEFAULT: '#303032',
  },
  red: {
    light: '#FFEEF1',
    DEFAULT: '#F52D4C',
  },
  green: {
    light: '#C7FFDD',
    DEFAULT: '#00AB44',
    dark: '#1D5C37',
  },
}

export const semanticColors = {
  pageBG: colors.white,
  pageText: colors.dark,
  header: {
    bg: colors.white,
    text: colors.dark,
  },
  box: {
    bg: colors.white,
    border: colors.dark,
  },
  btn: {
    primary: {
      text: {
        DEFAULT: colors.white,
        hover: colors.white,
      },
      bg: {
        DEFAULT: colors.dark,
        hover: colors.dark,
      },
      border: {
        DEFAULT: colors.transparent,
        hover: colors.transparent,
      },
    },
    secondary: {
      text: {
        DEFAULT: colors.dark,
        hover: colors.white,
      },
      bg: {
        DEFAULT: colors.transparent,
        hover: colors.dark,
      },
      border: {
        DEFAULT: colors.dark,
        hover: colors.dark,
      },
    },
    tertiary: {
      text: {
        DEFAULT: colors.dark,
        hover: colors.white,
      },
      bg: {
        DEFAULT: colors.transparent,
        hover: colors.dark,
      },
      border: {
        DEFAULT: colors.dark,
        hover: colors.dark,
      },
    },
  },
  input: {
    border: colors.dark,
    text: colors.dark,
    bg: colors.transparent,
    placeholder: colors.dark,
    inverted: {
      border: colors.white,
      text: colors.white,
      bg: colors.transparent,
      placeholder: colors.white,
    },
  },
  success: {
    DEFAULT: colors.green.DEFAULT,
    bg: colors.green.light,
  },
  error: {
    DEFAULT: colors.red.DEFAULT,
    bg: colors.red.light,
  },
}
